"use client";

import { CACHE_NAMES } from "./cache-constants";
import { recordDebugEvent } from "./debug";

/**
 * The PWA cache/refresh machinery shared by the trip and finance data
 * providers. Both apps follow the same model — cache-first paint from Cache
 * Storage, and a build-aware MANUAL refresh (manifest diff → pull only
 * changed bundles) — and this module is the single implementation of that
 * model. There is deliberately no background freshness probe and no
 * mount/interval/focus recheck: the apps touch the network only on a
 * first-ever load (nothing cached to paint) and when the user taps
 * Refresh / Sync Now.
 */

/* ── Cache Storage JSON helpers ─────────────────────────────────────────── */

export async function readCachedJson<T>(cacheName: string, url: string): Promise<T | null> {
  if (typeof caches === "undefined") return null;
  try {
    const res = await (await caches.open(cacheName)).match(url);
    return res ? ((await res.json()) as T) : null;
  } catch {
    return null;
  }
}

export async function writeCachedJson(cacheName: string, url: string, payload: unknown): Promise<void> {
  if (typeof caches === "undefined") return;
  try {
    const cache = await caches.open(cacheName);
    await cache.put(
      new Request(url),
      new Response(JSON.stringify(payload), { headers: { "Content-Type": "application/json" } }),
    );
  } catch {
    /* non-critical — cache unavailable */
  }
}

/* ── Last-synced timestamps (persisted in Cache Storage, never localStorage —
 *    the SW cache is the app's single offline store; see CLAUDE.md lesson 8) ── */

export async function writeCachedTimestamp(cacheName: string, key: string, ts: Date): Promise<void> {
  await writeCachedJson(cacheName, key, { ts: ts.toISOString() });
}

export async function readCachedTimestamp(cacheName: string, key: string): Promise<Date | null> {
  const payload = await readCachedJson<{ ts?: string | number }>(cacheName, key);
  if (!payload?.ts) return null;
  const d = new Date(payload.ts);
  return Number.isNaN(d.getTime()) ? null : d;
}

/* ── Auth ───────────────────────────────────────────────────────────────── */

/**
 * A 401 means the session expired — send the user to the LOGIN SCREEN (which
 * carries the path forward as ?callbackUrl so sign-in lands them back where
 * they were), but NEVER while offline: an offline device can't complete
 * OAuth, and the user may have perfectly good cached data to look at
 * (CLAUDE.md lesson 6). Not straight to Google: a 401 mid-render otherwise
 * left a dead "Failed to load data: 401" when the OAuth hop didn't happen
 * (lesson 29), and an explicit screen with a button is the right place for a
 * signed-out user to land anyway.
 */
export function handleUnauthorized(callbackUrl: string): void {
  if (typeof navigator !== "undefined" && !navigator.onLine) return;
  if (typeof window === "undefined") return;
  const target = loginUrl(callbackUrl);
  // Several fetches can 401 at once (a shard batch, the manifest) — one bounce.
  if (window.location.pathname === "/login") return;
  window.location.replace(target);
}

/** The login screen, remembering where to come back to. */
export function loginUrl(callbackUrl: string): string {
  return `/login?callbackUrl=${encodeURIComponent(callbackUrl)}`;
}

/* ── Service worker update ──────────────────────────────────────────────── */

/**
 * Force the service worker to check for a newer build and, if one is coming
 * in, wait until it takes control before the caller reloads — so a refresh
 * lands on the latest deployed assets in a single press instead of the usual
 * two-reload dance. Bounded by a timeout so it never hangs.
 */
export async function updateServiceWorker(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return;
    await reg.update();
    if (reg.installing || reg.waiting) {
      await new Promise<void>((resolve) => {
        navigator.serviceWorker.addEventListener("controllerchange", () => resolve(), { once: true });
        setTimeout(resolve, 4000);
      });
    }
  } catch {
    /* SW unavailable — the caller falls through to a plain reload */
  }
}

/* ── App update (decoupled from data refresh) ───────────────────────────── */

/**
 * Pull the newly deployed app: update the service worker, drop the runtime
 * page-shell cache (stale HTML referencing old chunk URLs), and reload onto
 * the new assets. Data caches are untouched — app assets and data are
 * independent — and the SW precache is NEVER deleted (it's only populated at
 * SW install; deleting it kills offline navigation until the next deploy).
 * Used by the header "Update app" action and the Debug panel's
 * "Update & reload".
 */
export async function updateApp(): Promise<void> {
  try {
    if (typeof caches !== "undefined") await caches.delete(CACHE_NAMES.pages);
  } catch {
    /* cache API unavailable */
  }
  await updateServiceWorker();
  window.location.reload();
}

/* ── Manifest-diffed incremental sync ───────────────────────────────────── */

/** The client-side manifest record: hashes of the bundles THIS device has
 * cached. Server manifests (which add `build`) are diffed against it. */
export interface AppliedManifest {
  schema: number;
  generatedAt: string;
  bundles: Record<string, string>;
}

/** Fetch a manifest, bounded and never HTTP-cached. Throws on !ok so callers
 * can route 401s to sign-in.
 *
 * Every manifest check is logged to the debug fetch log with its real
 * (gzipped) wire size. A manifest request is the one network call a Refresh /
 * Sync Now always makes — even when nothing changed — so recording it here,
 * once, keeps BOTH apps honest and identical: a refresh leaves exactly one
 * `manifest — N bytes` row, plus a row per bundle actually pulled. Previously
 * this call was invisible in the log, and the trip path papered over it with a
 * separate size-less "sync" summary row (which read as a network call with no
 * size), while finance logged nothing at all when up to date. */
export async function fetchManifest<T extends AppliedManifest & { build: string }>(
  url: string,
  debugKind = "manifest",
): Promise<T> {
  const t0 = performance.now();
  const res = await fetch(url, { cache: "no-store" });
  if (res.status === 401) {
    recordDebugEvent({ kind: debugKind, url, source: "error", status: 401, detail: "manifest check — unauthorized" });
    const err = new Error("401") as Error & { status?: number };
    err.status = 401;
    throw err;
  }
  if (!res.ok) {
    recordDebugEvent({ kind: debugKind, url, source: "error", status: res.status, detail: `manifest check — HTTP ${res.status}` });
    throw new Error(`HTTP ${res.status}`);
  }
  // Read as text first so we can report a byte size even when the response
  // carries no Content-Length (dev server, chunked): wire size when present
  // (the API gzips, so that's what actually came down), decoded length else.
  const wireBytes = parseInt(res.headers.get("content-length") ?? "", 10);
  const text = await res.text();
  recordDebugEvent({
    kind: debugKind,
    url,
    source: "network",
    durationMs: Math.round(performance.now() - t0),
    status: res.status,
    bytes: Number.isFinite(wireBytes) ? wireBytes : text.length,
    detail: "manifest check",
  });
  return JSON.parse(text) as T;
}

/** The bundle ids whose server hash differs from what this device has applied
 * (or that the applied manifest doesn't know about). A schema change marks
 * everything changed. Defensive against malformed manifests (deploy skew: a
 * server that doesn't speak manifests yet returns other shapes). */
export function diffManifest(applied: AppliedManifest | null, fresh: AppliedManifest): string[] {
  const ids = Object.keys(fresh.bundles ?? {});
  if (!applied || applied.schema !== fresh.schema) return ids;
  return ids.filter((id) => applied.bundles[id] !== fresh.bundles[id]);
}

/* ── Idle scheduling ────────────────────────────────────────────────────── */

/** Run low-priority work after paint without competing with rendering. */
export function scheduleIdle(fn: () => void): void {
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(() => fn(), { timeout: 4000 });
  } else {
    setTimeout(fn, 1000);
  }
}
